import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlayCircle, faTimes, faClock, faRocket, faChartLine, faTools, faBrain, faUserFriends, faRobot, faLightbulb, faBullseye } from '@fortawesome/free-solid-svg-icons';
import { faYoutube as faBrandYoutube } from '@fortawesome/free-brands-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import IndieSubmenu from '../../component/section/publishers/IndieSubmenu';

function WorkSmarterMarketingPage() {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Sample video data - you can replace with your actual videos
    const allVideos = [
        {
            id: 'F4LMtCtI_d8',
            title: 'How To Source ACTIVE Streamers Who Have Played Games Similar To Yours In Minutes',
            description: 'Influencers come with three problems: 1) Are they active? 2) How do you contact them (email)? 3) How do you know they will like my game? All questions answered by creating look alike audiences by search gaming that been play recently in the past month, and finding the contact information of those streamers and influencers in a few minutes.',
            thumbnail: 'https://img.youtube.com/vi/F4LMtCtI_d8/maxresdefault.jpg',
            duration: '5:22',
            category: 'Influencer Marketing',
            date: '2023-10-15'
        },
        {
            id: 'LjZ5vSKunvE',
            title: 'Using AI To Post Accurate Game Lore On Social Media in 30 Seconds',
            description: 'As a marketer, you\'re probably working on multiple games at the same time, and each one has its own vibrant lore and story. The problem is, that lore can sometimes be overwhelmingly long and complex—leaving you with hours of reading just to sift through it and match it correctly to your content. Even harder, you have to make sure what you\'re posting is accurate. As marketers ourselves, we\'ve messed up a few times trying to get every detail right—motivations, names, the tiny but important things. It all matters. Seriously, we\'ve had Google Drives full of lore that could fill a history book. So, we started using AI that could accurately take in the lore, understand what the game is about, and also grasp the context of the media it\'s being paired with. The result? No more small blunders and contextually relevant content made with ease.',
            thumbnail: 'https://img.youtube.com/vi/LjZ5vSKunvE/maxresdefault.jpg',
            duration: '7:45',
            category: 'Content Creation',
            date: '2023-11-02'
        },
        {
            id: 'D-5AtFWy3aw',
            title: 'How To Post To Multiple Subreddits In Minutes',
            description: 'Learn how to find the subreddits you can post to without getting banned or flagged for promotion, based on your game\'s genre, and post to all of them at once.',
            thumbnail: 'https://img.youtube.com/vi/D-5AtFWy3aw/maxresdefault.jpg',
            duration: '2:44',
            category: 'Social Media',
            date: '2025-07-08'
        },
        // Add more videos as needed
    ];

    const [filteredVideos, setFilteredVideos] = useState(allVideos);

    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredVideos(allVideos);
        } else {
            const query = searchQuery.toLowerCase();
            setFilteredVideos(
                allVideos.filter(
                    video =>
                        video.title.toLowerCase().includes(query) ||
                        video.description.toLowerCase().includes(query) ||
                        video.category.toLowerCase().includes(query)
                )
            );
        }
    }, [searchQuery, allVideos]);

    const openVideoModal = (video) => {
        setSelectedVideo(video);
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
    };

    const closeVideoModal = () => {
        setIsModalOpen(false);
        document.body.style.overflow = 'auto'; // Re-enable scrolling
    };

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Work Smarter with AI: The Ultimate Game Marketing Series | Glitch Gaming</title>
                <meta
                    name="description"
                    content="Discover AI-powered tools and strategies to scale your game marketing. Our video series shows you how to work smarter, not harder, with artificial intelligence."
                />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="Work Smarter with AI: The Ultimate Game Marketing Series | Glitch" />
                <meta property="og:description" content="Discover AI-powered tools and strategies to scale your game marketing. Our video series shows you how to work smarter, not harder, with artificial intelligence." />
                <meta property="og:type" content="website" data-rh="true" />
                <meta property="og:url" content={currentUrl} data-rh="true" />
                <meta property="og:image" content="https://www.glitch.fun/assets/images/publishers/work_smarter_banner.webp" data-rh="true" />
            </Helmet>
            <Header />
            
            {/* Hero Section */}
            <section className="work-smarter-hero">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 text-center text-lg-start">
                            <h1 className="display-4 fw-bold mb-4 pt-5">
                                Work <span className="text-gradient">Smarter</span>, Not Harder With AI
                            </h1>
                            <p className="lead mb-5">Watch our video for tips and tricks on how AI can improve your workflow, accuracy, and the impact of your game's marketing.</p>
                           
                        </div>
                        <div className="col-lg-6">
                            <div className="hero-visual mt-5 mt-lg-0 pt-5">
                                <img
                                    src="/assets/images/workharder/work-smarter-1.png"
                                    alt="Work Smarter, Not Harder with AI"
                                    className="img-fluid rounded-3 shadow-lg border border-3 border-white"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Video Search and Grid */}
            <section className="video-series py-5 bg-light">
                <div className="container">
                    <div className="row justify-content-center mb-5">
                        <div className="col-lg-8 text-center">
                                <h3 className='text-black'>Video Tips and Tricks</h3>
                            <div className="search-container position-relative ">
                                
                                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                <input
                                    type="text"
                                    className="form-control form-control-lg search-input"
                                    placeholder="Search videos by title, topic, or keyword..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {filteredVideos.length === 0 ? (
                        <div className="text-center py-5">
                            <h3>No videos found matching your search</h3>
                            <p className="lead">Try a different search term or browse all videos</p>
                            <button 
                                className="btn btn-outline-primary"
                                onClick={() => setSearchQuery('')}
                            >
                                Clear Search
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="row g-4">
                                {filteredVideos.map((video) => (
                                    <div className="col-md-6 col-lg-4" key={video.id}>
                                        <div 
                                            className="video-card h-100"
                                            onClick={() => openVideoModal(video)}
                                        >
                                            <div className="video-thumbnail">
                                                <img
                                                    src={video.thumbnail}
                                                    alt={video.title}
                                                    className="img-fluid rounded-top"
                                                />
                                                <div className="play-button">
                                                    <FontAwesomeIcon icon={faPlayCircle} />
                                                </div>
                                                <div className="video-duration">
                                                    <FontAwesomeIcon icon={faClock} className="me-1" />
                                                    {video.duration}
                                                </div>
                                            </div>
                                            <div className="video-info p-4">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span className="badge bg-primary">{video.category}</span>
                                                    <small className="text-muted">{video.date}</small>
                                                </div>
                                                <h3 className="h5 mb-3 text-black">{video.title}</h3>
                                                <p className="text-muted">{video.description.substring(0, 120)}...</p>
                                                <div className="d-flex align-items-center mt-3">
                                                    <FontAwesomeIcon icon={faBrandYoutube} className="text-danger me-2" />
                                                    <small>Watch on YouTube</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="text-center mt-5">
                                <div className="alert alert-info">
                                    <h4 className="mb-3 text-black">Want more videos?</h4>
                                    <p className="mb-0">We're constantly adding new content to this series. Subscribe to our newsletter to get notified when new videos are released.</p>
                                    
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>

            {/* The AI Advantage Section */}
            <section className="py-5 text-black">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="display-5 fw-bold mb-3">The AI Advantage in Game Marketing</h2>
                        <p className="lead text-white">AI is transforming how companies engage with their audiences, making marketing more intelligent, data-driven, and responsive to individual player needs.</p>
                    </div>
                    
                    <div className="row g-4">
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card p-4 rounded-3 h-100">
                                <div className="icon-wrapper bg-primary mb-3">
                                    <FontAwesomeIcon icon={faBullseye} size="2x" />
                                </div>
                                <h3 className='text-black'>Hyper-Personalization at Scale</h3>
                                <p>Move beyond simple genre tags. AI analyzes player behavior, purchase history, and even in-game actions to deliver hyper-targeted campaigns to the right audience at the perfect time, creating a seamless, personalized player journey.</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card p-4 rounded-3 h-100">
                                <div className="icon-wrapper bg-success mb-3">
                                    <FontAwesomeIcon icon={faChartLine} size="2x" />
                                </div>
                                <h3 className='text-black'>Predictive Analytics & Forecasting</h3>
                                <p>Imagine accurately forecasting wishlist conversions or predicting player churn. AI models analyze historical data and market trends to give you a strategic edge, helping you manage resources and optimize your launch strategy.</p>
                            </div>
                        </div>
                         <div className="col-md-6 col-lg-4">
                            <div className="feature-card p-4 rounded-3 h-100">
                                <div className="icon-wrapper bg-warning mb-3">
                                    <FontAwesomeIcon icon={faRobot} size="2x" />
                                </div>
                                <h3 className='text-black'>Automated Content Creation</h3>
                                <p>Scale your messaging without scaling your team. Generate endless variations of ad copy, draft press releases, and create lore-accurate social media posts in seconds, freeing you up to focus on high-level strategy and community engagement.</p>
                            </div>
                        </div>
                         <div className="col-md-6 col-lg-4">
                            <div className="feature-card p-4 rounded-3 h-100">
                                <div className="icon-wrapper bg-info mb-3">
                                    <FontAwesomeIcon icon={faBrain} size="2x" />
                                </div>
                                <h3 className='text-black'>Data-Driven Player Insights</h3>
                                <p>Stop guessing what players want. AI can process and analyze huge amounts of unstructured data from Discord, Reddit, and Steam reviews to extract actionable insights on player sentiment, feature requests, and emerging trends.</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card p-4 rounded-3 h-100">
                                <div className="icon-wrapper bg-danger mb-3">
                                    <FontAwesomeIcon icon={faUserFriends} size="2x" />
                                </div>
                                <h3 className='text-black'>Smarter Influencer Discovery</h3>
                                <p>Finding the right content creators is a huge drain on time. AI-powered tools can identify active influencers who have played similar games, analyze their audience demographics, and even predict their potential ROI for your campaign in minutes, not days.</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card p-4 rounded-3 h-100">
                                <div className="icon-wrapper bg-secondary mb-3">
                                    <FontAwesomeIcon icon={faLightbulb} size="2x" />
                                </div>
                                <h3 className='text-black'>Enhanced Creativity</h3>
                                <p>AI is more than an automation tool; it's a creative partner. Use it to brainstorm campaign angles, generate visual concepts, write video scripts, and overcome creative blocks, ensuring your marketing is always fresh and innovative.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quote Section */}
            <section className="py-5 bg-dark text-white">
                <div className="container">
                    <div className="text-center">
                        <p className="lead fst-italic">"Your job will not be taken by AI. It will be taken by a person who knows how to use AI. It is very important for marketers to know how to use AI."</p>
                        <h4 className="fw-bold mt-3">- Christina Inge</h4>
                        <p className="text-white-50">Author & Instructor, Harvard Division of Continuing Education</p>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-5 bg-primary text-white">
                <div className="container text-center">
                    <h2 className="mb-4">Ready to Leverage AI for Your Game's Success?</h2>
                    <p className="lead mb-5">Stop the manual grind. Start building smarter, data-driven marketing campaigns that deliver real results and connect your game with millions of players.</p>
                    <div className="d-flex justify-content-center gap-3">
                        <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-light btn-lg px-5">
                            Start Free Trial
                        </Link>
                        <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg px-5">
                            Book Demo
                        </Link>
                    </div>
                </div>
            </section>

            {/* Video Modal */}
            {isModalOpen && selectedVideo && (
                <div className="video-modal-backdrop" onClick={closeVideoModal}>
                    <div className="video-modal-container" onClick={(e) => e.stopPropagation()}>
                        <button className="close-button" onClick={closeVideoModal}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        <div className="video-wrapper">
                            <iframe
                                src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1`}
                                title={selectedVideo.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                        <div className="video-info p-4">
                            <h3 className='text-black'>{selectedVideo.title}</h3>
                            <p className="text-muted">{selectedVideo.description}</p>
                        </div>
                    </div>
                </div>
            )}

            <IndieSubmenu />
            <Footer />

            <style jsx>{`
                .work-smarter-hero {
                    padding: 100px 0;
                    background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
                    color: white;
                }

                .text-gradient {
                    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .hero-visual {
                    position: relative;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
                }

                .search-container {
                    position: relative;
                }

                .search-icon {
                    position: absolute;
                    left: 20px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #6c757d;
                    z-index: 10;
                }

                .search-input {
                    padding-left: 50px;
                    border-radius: 50px;
                    border: 2px solid rgba(0,0,0,0.1);
                    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
                }

                .video-card {
                    background: white;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    cursor: pointer;
                }

                .video-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 25px rgba(0,0,0,0.15);
                }

                .video-thumbnail {
                    position: relative;
                    overflow: hidden;
                }

                .video-thumbnail img {
                    width: 100%;
                    height: 200px;
                    object-fit: cover;
                    transition: transform 0.3s ease;
                }

                .video-card:hover .video-thumbnail img {
                    transform: scale(1.05);
                }

                .play-button {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: white;
                    font-size: 3rem;
                    opacity: 0.8;
                    transition: opacity 0.3s ease;
                }

                .video-card:hover .play-button {
                    opacity: 1;
                }

                .video-duration {
                    position: absolute;
                    bottom: 10px;
                    right: 10px;
                    background: rgba(0,0,0,0.7);
                    color: white;
                    padding: 3px 8px;
                    border-radius: 4px;
                    font-size: 0.8rem;
                }

                .video-info {
                    background: white;
                }

                .icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 1.5rem;
                    color: white;
                }

                .feature-card {
                    background: white;
                    border: 1px solid rgba(0,0,0,0.05);
                    transition: transform 0.3s ease;
                }

                .feature-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                }

                /* Video Modal Styles */
                .video-modal-backdrop {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0,0,0,0.85);
                    backdrop-filter: blur(8px);
                    z-index: 1050;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                }

                .video-modal-container {
                    position: relative;
                    width: 100%;
                    max-width: 900px;
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04);
                }

                .close-button {
                    position: absolute;
                    top: 15px;
                    right: 15px;
                    background: rgba(0,0,0,0.5);
                    border: none;
                    color: white;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10;
                    cursor: pointer;
                    transition: background 0.3s ease;
                }

                .close-button:hover {
                    background: rgba(0,0,0,0.8);
                }

                .video-wrapper {
                    position: relative;
                    padding-bottom: 56.25%; /* 16:9 aspect ratio */
                    height: 0;
                    overflow: hidden;
                }

                .video-wrapper iframe {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border: none;
                }

                @media (max-width: 992px) {
                    .feature-card {
                        text-align: center;
                    }
                    .icon-wrapper {
                        margin-left: auto;
                        margin-right: auto;
                    }
                }
                
                @media (max-width: 768px) {
                    .work-smarter-hero {
                        padding: 60px 0;
                    }
                    
                    .hero-visual {
                        margin-top: 2rem;
                    }
                    
                    .video-card {
                        margin-bottom: 1.5rem;
                    }
                    
                    .video-modal-container {
                        width: 95%;
                    }
                }
            `}</style>
        </>
    );
}

export default WorkSmarterMarketingPage;